import React from 'react';
import styled from '@emotion/styled';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { useModal } from 'ui/components/screens/modals/useModal';
import { CompanyNameSearch } from 'ui/components/screens/modals/CompanyNameSearch/CompanyNameSearch';
import { TemplateModal } from 'ui/components/screens/modals/TemplateModal/TemplateModal';
import { useSupporterCompanyEdit } from './useSupporterCompanyEdit';
import { mqUser } from 'utils/responsive';
import { CompanyEditForm } from 'ui/components/company/patterns/CompanyEditForm/CompanyEditForm';

const BgWrapper = styled.div`
    padding: 18px 50px;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    text-align: left;
    background-color: #f8f9fd;
    height: auto;
    min-height: calc(100vh - var(--height-pc-header));

    ${mqUser('SP')} {
        padding: 18px 15px;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding-bottom: 40px;
`;

const ErrorMessage = styled.div`
    background-color: #fee;
    color: #c33;
    padding: 16px;
    border-radius: 4px;
    margin: 20px 0;
    text-align: center;
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: var(--font-medium);
`;

/**
 * サポーター用企業プロフィール編集ページ
 */
export const SupporterCompanyEdit: React.FC = () => {
    const {
        control,
        loading,
        errors,
        areaChoices,
        filteredPrefectureChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        editing,
        user,
        snackBarMessage,
        showSnackbar,
        setEditing,
        setShowSnackbar,
        submit,
        setValue,
        watch,
        onCompanySelect,
        error,
        companyType,
    } = useSupporterCompanyEdit();

    const companySearchModal = useModal();
    const templateModal = useModal();

    const handleSelectTemplate = (text: string) => {
        const currentQuestion = watch('question') || '';
        if (currentQuestion.trim()) {
            setValue('question', currentQuestion + '\n' + text);
        } else {
            setValue('question', text);
        }
    };

    if (loading && !user) {
        return (
            <BgWrapper>
                <Wrapper>
                    <PageTitle title="企業プロフィール編集" />
                    <LoadingMessage>読み込み中...</LoadingMessage>
                </Wrapper>
            </BgWrapper>
        );
    }

    if (error) {
        return (
            <BgWrapper>
                <Wrapper>
                    <PageTitle title="企業プロフィール編集" />
                    <ErrorMessage>{error}</ErrorMessage>
                </Wrapper>
            </BgWrapper>
        );
    }

    if (!user || companyType !== 'seller') {
        return (
            <BgWrapper>
                <Wrapper>
                    <PageTitle title="企業プロフィール編集" />
                    <ErrorMessage>
                        {companyType !== 'seller' ? '売り手企業のみ編集可能です' : '企業情報が見つかりません'}
                    </ErrorMessage>
                </Wrapper>
            </BgWrapper>
        );
    }

    return (
        <BgWrapper>
            {companySearchModal.createModal(
                <CompanyNameSearch
                    onSelect={(company) => onCompanySelect(company)}
                    closeModal={companySearchModal.closeModal}
                />,
                { scrollable: false },
            )}
            {templateModal.createModal(
                <TemplateModal
                    onSelect={handleSelectTemplate}
                    onClose={templateModal.closeModal}
                    companyType="company"
                />,
                {
                    keyboard: true,
                    maskClosable: true,
                    onClose: templateModal.closeModal,
                },
            )}
            <Wrapper>
                <SnackBar message={snackBarMessage} open={showSnackbar} setOpen={setShowSnackbar} />
                <div>
                    <PageTitle title="企業プロフィール編集" />
                </div>
                <CompanyEditForm
                    user={user}
                    control={control}
                    loading={loading}
                    errors={errors}
                    areaChoices={areaChoices}
                    employeeChoices={employeeChoices}
                    revenueChoices={revenueChoices}
                    industryChoices={industryChoices}
                    netAssetsLevelChoices={netAssetsLevelChoices}
                    filteredPrefectureChoices={filteredPrefectureChoices}
                    profitLevelChoices={profitLevelChoices}
                    editing={editing}
                    submit={submit}
                    setValue={setValue}
                    watch={watch}
                    onCompanySelect={onCompanySelect}
                    setEditing={setEditing}
                    showSnackbar={showSnackbar}
                    setShowSnackbar={setShowSnackbar}
                />
            </Wrapper>
        </BgWrapper>
    );
};
